/**
 * Layered lobby-tile art: a game's tile drawn as 2–4 stacked images that shift
 * by different amounts with the pointer (or the scroll on touch screens), so the
 * tile reads as having depth. A layer marked `popout` may also rise past the
 * tile's top and sides ("break the frame").
 *
 * Stored as `game.games.metadata.layers`, back to front. Layer 0 is the
 * background and is always clipped to the tile. Placement is in percent of the
 * tile: `left` and `width` of its width, `top` of its height; the image keeps
 * its own aspect ratio. With no placement a layer covers the tile from its
 * top-left corner at full width. A layer may name a looping `motion` that plays
 * only while the tile is active, so the art moves inside the picture.
 *
 * LEAF module like upload-contexts: imports nothing, so both SPAs can take it
 * through `@b1fr0st/contracts/game-layers` without server dependencies.
 */

export const GAME_LAYER_MOTIONS = ['float', 'sway', 'pulse', 'twinkle', 'spin'] as const;
export type GameLayerMotion = (typeof GAME_LAYER_MOTIONS)[number];

export interface GameLayer {
  url: string;
  /** How far the layer travels relative to the others: 0 = still, 1 = the full depth. */
  depth: number;
  /** May rise past the tile's top and sides when the tile is active. */
  popout?: boolean;
  /** Percent of tile width. Negative values start the layer left of the tile. */
  left?: number;
  /** Percent of tile height. */
  top?: number;
  /** Percent of tile width; the height follows the image's own aspect ratio. */
  width?: number;
  /** Looping movement the layer plays while the tile is active. */
  motion?: GameLayerMotion;
}

export const GAME_LAYER_LIMITS = {
  minLayers: 2,
  maxLayers: 4,
  minDepth: 0,
  maxDepth: 1.5,
  /** Placement bounds, in percent. Wide enough for oversized and bleeding layers. */
  minOffset: -100,
  maxOffset: 200,
  minWidth: 10,
  maxWidth: 300,
} as const;

function finiteIn(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

function isMotion(value: unknown): value is GameLayerMotion {
  return typeof value === 'string' && (GAME_LAYER_MOTIONS as readonly string[]).includes(value);
}

function isHttpsUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 1024) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * The layers a tile can draw, or null when the stored value is missing or does
 * not describe a drawable stack. A reader treats null as "draw the single
 * thumbnail", so one malformed row degrades to the old tile instead of breaking
 * the lobby.
 */
export function parseGameLayers(value: unknown): GameLayer[] | null {
  if (!Array.isArray(value)) return null;
  const L = GAME_LAYER_LIMITS;
  if (value.length < L.minLayers || value.length > L.maxLayers) return null;
  const layers: GameLayer[] = [];
  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null) return null;
    const r = raw as Record<string, unknown>;
    if (!isHttpsUrl(r.url) || !finiteIn(r.depth, L.minDepth, L.maxDepth)) return null;
    const layer: GameLayer = { url: r.url, depth: r.depth };
    if (r.popout !== undefined) {
      if (typeof r.popout !== 'boolean') return null;
      layer.popout = r.popout;
    }
    for (const key of ['left', 'top'] as const) {
      if (r[key] === undefined) continue;
      if (!finiteIn(r[key], L.minOffset, L.maxOffset)) return null;
      layer[key] = r[key] as number;
    }
    if (r.width !== undefined) {
      if (!finiteIn(r.width, L.minWidth, L.maxWidth)) return null;
      layer.width = r.width;
    }
    if (r.motion !== undefined) {
      if (!isMotion(r.motion)) return null;
      layer.motion = r.motion;
    }
    layers.push(layer);
  }
  return layers;
}
